import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X } from "lucide-react";

const CATS = ["Light Jet", "Midsize Jet", "Super Midsize", "Heavy Jet", "Ultra Long Range", "VIP Airliner"];
const inp = "w-full bg-white border border-[#E5DFD2] focus:border-[#A47E2A] focus:outline-none px-3 py-2 rounded-sm text-sm text-[#1A1D24]";

const emptyForm = () => ({
  tail_number: "", model: "", manufacturer: "", category: "Light Jet",
  max_passengers: 8, range_nm: 2000, cruise_speed_kts: 450, base: "",
  hourly_rate_usd: 5000, year: new Date().getFullYear(),
  image_url: "https://images.unsplash.com/photo-1540962351504-03099e0a754b?auto=format&fit=crop&w=1400&q=80",
  features: [], available: true,
});

export default function OpsFleet() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // null | object (form)
  const [featuresInput, setFeaturesInput] = useState("");

  const load = () => api.get("/fleet").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...emptyForm(), id: null }); setFeaturesInput(""); };
  const openEdit = (a) => { setEditing({ ...a }); setFeaturesInput((a.features || []).join(", ")); };
  const close = () => { setEditing(null); setFeaturesInput(""); };

  const save = async () => {
    const { id, ...rest } = editing;
    const payload = {
      ...rest,
      max_passengers: parseInt(rest.max_passengers) || 0,
      range_nm: parseInt(rest.range_nm) || 0,
      cruise_speed_kts: parseInt(rest.cruise_speed_kts) || 0,
      hourly_rate_usd: parseInt(rest.hourly_rate_usd) || 0,
      year: parseInt(rest.year) || new Date().getFullYear(),
      features: featuresInput.split(",").map(s => s.trim()).filter(Boolean),
    };
    try {
      if (id) {
        await api.put(`/fleet/${id}`, payload);
        toast.success("Aircraft updated");
      } else {
        await api.post(`/fleet`, payload);
        toast.success("Aircraft added");
      }
      close();
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map(x => x?.msg || "").filter(Boolean).join("; ") : (typeof d === "string" ? d : "Save failed");
      toast.error(msg);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete ${a.model} (${a.tail_number})?`)) return;
    try {
      await api.delete(`/fleet/${a.id}`);
      toast.success("Aircraft deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <DashboardLayout role={user?.role}>
      <div className="max-w-[1400px] mx-auto px-8 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="as-overline">Fleet</div>
            <h1 className="as-serif text-5xl font-light mt-2" data-testid="ops-fleet-title">Marketplace Aircraft</h1>
            {!isAdmin && <div className="mt-3 text-xs text-[#756E64]">Read-only for Operations · contact Admin for changes.</div>}
          </div>
          {isAdmin && (
            <button onClick={openNew} className="as-btn-gold px-5 py-3 rounded-sm as-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2" data-testid="fleet-add-btn">
              <Plus size={13} /> Add Aircraft
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="ops-fleet-grid">
          {rows.map(a => (
            <div key={a.id} className="as-card overflow-hidden" data-testid={`fleet-row-${a.id}`}>
              <img src={a.image_url} className="w-full aspect-video object-cover" alt="" />
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64]">{a.manufacturer} · {a.category}</div>
                    <div className="as-serif text-xl mt-1">{a.model} <span className="as-mono text-xs as-gold ml-2">{a.tail_number}</span></div>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(a)} className="p-1.5 text-[#756E64] hover:text-[#A47E2A] hover:bg-[#F1EDE4] rounded-sm" data-testid={`fleet-edit-${a.id}`}><Edit3 size={14} /></button>
                      <button onClick={() => remove(a)} className="p-1.5 text-[#756E64] hover:text-red-600 hover:bg-red-50 rounded-sm" data-testid={`fleet-delete-${a.id}`}><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>
                <div className="mt-3 flex justify-between text-xs">
                  <div><span className="text-[#756E64]">Pax</span> <span className="text-[#1A1D24]">{a.max_passengers}</span></div>
                  <div><span className="text-[#756E64]">Range</span> <span className="text-[#1A1D24]">{a.range_nm} nm</span></div>
                  <div><span className="text-[#756E64]">Base</span> <span className="text-[#1A1D24]">{a.base}</span></div>
                </div>
                <div className="mt-3 text-lg as-gold as-serif">${a.hourly_rate_usd?.toLocaleString()}<span className="as-mono text-[10px] text-[#756E64] ml-1">/ hr</span></div>
              </div>
            </div>
          ))}
        </div>

        {editing && (
          <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={close} data-testid="fleet-modal">
            <div onClick={e => e.stopPropagation()} className="bg-white rounded-md border border-[#E5DFD2] w-full max-w-2xl shadow-xl">
              <div className="flex items-center justify-between p-5 border-b border-[#E5DFD2]">
                <div className="as-serif text-2xl">{editing.id ? "Edit Aircraft" : "Add Aircraft"}</div>
                <button onClick={close} className="text-[#756E64] hover:text-[#1A1D24]" data-testid="fleet-modal-close"><X size={18}/></button>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
                <Field label="Tail Number"><input value={editing.tail_number} onChange={e => setEditing({...editing, tail_number: e.target.value})} className={inp} data-testid="fleet-tail"/></Field>
                <Field label="Manufacturer"><input value={editing.manufacturer} onChange={e => setEditing({...editing, manufacturer: e.target.value})} className={inp} data-testid="fleet-mfr"/></Field>
                <Field label="Model"><input value={editing.model} onChange={e => setEditing({...editing, model: e.target.value})} className={inp} data-testid="fleet-model"/></Field>
                <Field label="Category">
                  <select value={editing.category} onChange={e => setEditing({...editing, category: e.target.value})} className={inp} data-testid="fleet-category">
                    {CATS.map(c => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Year"><input type="number" value={editing.year} onChange={e => setEditing({...editing, year: e.target.value})} className={inp} data-testid="fleet-year"/></Field>
                <Field label="Home Base (ICAO)"><input value={editing.base} onChange={e => setEditing({...editing, base: e.target.value.toUpperCase()})} className={inp} data-testid="fleet-base"/></Field>
                <Field label="Max Passengers"><input type="number" value={editing.max_passengers} onChange={e => setEditing({...editing, max_passengers: e.target.value})} className={inp} data-testid="fleet-pax"/></Field>
                <Field label="Range (nm)"><input type="number" value={editing.range_nm} onChange={e => setEditing({...editing, range_nm: e.target.value})} className={inp} data-testid="fleet-range"/></Field>
                <Field label="Cruise Speed (kts)"><input type="number" value={editing.cruise_speed_kts} onChange={e => setEditing({...editing, cruise_speed_kts: e.target.value})} className={inp} data-testid="fleet-speed"/></Field>
                <Field label="Hourly Rate (USD)"><input type="number" value={editing.hourly_rate_usd} onChange={e => setEditing({...editing, hourly_rate_usd: e.target.value})} className={inp} data-testid="fleet-rate"/></Field>
                <Field label="Image URL" full><input value={editing.image_url} onChange={e => setEditing({...editing, image_url: e.target.value})} className={inp} data-testid="fleet-image"/></Field>
                <Field label="Features (comma-separated)" full><input value={featuresInput} onChange={e => setFeaturesInput(e.target.value)} className={inp} placeholder="Wi-Fi, Enclosed lavatory, Full galley" data-testid="fleet-features"/></Field>
              </div>
              <div className="p-5 border-t border-[#E5DFD2] flex justify-end gap-3">
                <button onClick={close} className="as-btn-ghost px-4 py-2 rounded-sm as-mono text-[10px] uppercase tracking-widest">Cancel</button>
                <button onClick={save} className="as-btn-gold px-5 py-2 rounded-sm as-mono text-[10px] uppercase tracking-widest" data-testid="fleet-save">{editing.id ? "Save changes" : "Add aircraft"}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
function Field({ label, children, full }) {
  return (
    <label className={full ? "md:col-span-2" : ""}>
      <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
